import { continentDescMap } from 'everywhere/daily/container/common';
import useThemes, { ITheme } from 'hooks/useThemes';
import React, { Dispatch, SetStateAction, useContext, useState } from 'react';

enum OrderType {
  rankDesc = 'rkd',
  priceDesc = 'prd',
  durationDesc = 'dud',
}

enum ContinentType {
  ALL = '',
  DOMESTIC = '국내',
  SOUTHEAST_ASIA = '동남아',
  CHINA = '중국',
  JAPAN = '일본',
  AMERICA = '미주',
  EUROPE = '유럽',
  OCEANIA = '남태평양',
  ASIA = '아시아',
  MIDDLE_EAST = '중동',
  SOUTH_AMERICA = '남미',
  AFRICA = '아프리카',
}

const allSortList = new Map([
  [OrderType.rankDesc, '인기순'],
  [OrderType.priceDesc, '가격순'],
  [OrderType.durationDesc, '소요시간순'],
]);

const allContinentList = new Map([
  [[ContinentType.DOMESTIC], '국내'],
  [[ContinentType.SOUTHEAST_ASIA], '동남아'],
  [[ContinentType.JAPAN], '일본'],
  [[ContinentType.CHINA], '중국'],
  [[ContinentType.EUROPE], '유럽'],
  [[ContinentType.OCEANIA], '남태평양'],
  [[ContinentType.ASIA], '아시아'],
  [[ContinentType.AMERICA], '미주'],
  [
    [
      ContinentType.MIDDLE_EAST,
      ContinentType.SOUTH_AMERICA,
      ContinentType.AFRICA,
    ],
    '기타',
  ],
]);

export interface IRecommendSearchParams {
  continents: Set<string>;
  countries: string;
  duration: string;
  fare: string;
  isDirect: boolean;
  months: Set<string>;
  roundTripTime: string;
  theme: string;
}

export enum RecommendModal {
  NULL = 'NUL',
  Theme = 'Thm',
  Filter = 'Flt',
}

interface IContextValue {
  recommendSearchParams: IRecommendSearchParams;
  recommendModal: string;
  setRecommendSearchParams: Dispatch<SetStateAction<IRecommendSearchParams>>;
  setRecommendModal: Dispatch<SetStateAction<RecommendModal>>;
}

export enum DefaultValue {
  MIN_DURATION = 1,
  MAX_DURATION = 24,
  MIN_ROUND_TRIP_TIME = 1,
  MAX_ROUND_TRIP_TIME = 15,
  MIN_FARE = 10000,
  MAX_FARE = 3000000,
}

const defaultRecommendSearchParams = {
  isDirect: false,
  theme: '',
  continents: new Set<string>(),
  countries: '',
  months: new Set<string>(),
  duration: [DefaultValue.MIN_DURATION, DefaultValue.MAX_DURATION].toString(),
  fare: [DefaultValue.MIN_FARE, DefaultValue.MAX_FARE].toString(),
  roundTripTime: [
    DefaultValue.MIN_ROUND_TRIP_TIME,
    DefaultValue.MAX_ROUND_TRIP_TIME,
  ].toString(),
};

const RecommendParamsContext = React.createContext<IContextValue>({
  recommendSearchParams: defaultRecommendSearchParams,
  recommendModal: RecommendModal.NULL,
  setRecommendSearchParams: () => {},
  setRecommendModal: () => {},
});

interface IProps {
  children: React.ReactNode;
  continents?: string;
  country?: string;
  months?: string;
  theme?: string;
}

function RecommendParamsContextProvider({
  children,
  continents,
  country,
  months,
  theme,
}: IProps) {
  const [recommendSearchParams, setRecommendSearchParams] =
    useState<IRecommendSearchParams>({
      ...defaultRecommendSearchParams,
      continents: new Set(
        continents?.split(',').flatMap(continent => {
          const continentDesc = continentDescMap.get(+continent);
          return continentDesc ? continentDesc : [];
        }),
      ),
      countries: country || '',
      months: new Set(months?.split(',')),
      theme: theme || '',
    });
  const [recommendModal, setRecommendModal] = useState(RecommendModal.NULL);

  return (
    <RecommendParamsContext.Provider
      value={{
        recommendSearchParams,
        recommendModal,
        setRecommendSearchParams,
        setRecommendModal,
      }}
    >
      {children}
    </RecommendParamsContext.Provider>
  );
}

function useRecommendParamsContext() {
  return useContext(RecommendParamsContext);
}

function getRecommendParams(searchParams: IRecommendSearchParams) {
  const {
    continents,
    countries,
    duration,
    fare,
    isDirect,
    months,
    roundTripTime,
  } = searchParams;
  const allThemes: ITheme[] = useThemes();
  return {
    continents:
      continents.size > 0 ? Array.from(continents).join(',') : undefined,
    countries: countries || undefined,
    duration,
    fare,
    roundTripTime,
    sdate: months.size > 0 ? Array.from(months).join(',') : undefined,
    stops: isDirect ? 0 : undefined,
    themes: searchParams.theme
      ? allThemes.find(
          theme =>
            theme.theme_id !== 0 &&
            theme.theme_id.toString() === searchParams.theme,
        )?.theme_name
      : undefined,
  };
}

export {
  ContinentType,
  OrderType,
  RecommendParamsContext,
  RecommendParamsContextProvider,
  allContinentList,
  allSortList,
  defaultRecommendSearchParams,
  getRecommendParams,
  useRecommendParamsContext,
};
